import { Checkbox as MantineCheckBox } from '@mantine/core'
import { sortByKey } from '@semios/app-platform-common'
import type { TPermission } from '@semios/app-platform-value-type-definitions'
import { Checkbox } from 'components/Checkbox/Checkbox'
import { MultiSelect } from 'components/MultiSelect/MultiSelect'
import type { TOptions } from 'components/MultiSelect/MultiSelect.types'
import { translate } from 'i18n/i18n'
import type { Dispatch, SetStateAction } from 'react'
import { useEffect, useMemo } from 'react'
import { fieldAssetStore } from 'stores/fieldAssetStore/fieldAssetStore'
import type { TProperty } from 'stores/fieldAssetStore/helperTypes'
import { checkAuthorization } from 'utils/checkAuthorization'
import { constantCaseify } from 'utils/constantCaseify'
import { getBlocksFromProperties } from 'utils/getBlocksFromProperties'
import { getPointExternalVendorNames } from 'utils/getPointExternalVendorNames'
import { getPointsByValuesCurrentKey } from 'utils/getPointsByValuesCurrentKey'
import type { TOptionsWithExternalVendors } from 'utils/makeExternalVendorMultiSelectProps'
import { makeExternalVendorMultiSelectProps } from 'utils/makeExternalVendorMultiSelectProps'
import type { AlertTypeId, WeatherForecastAlertOnly } from '../../../../_types'
import { AlertSection } from '../../../../components/AlertSection'
import { ErrorTextWrapper } from '../../../../components/ErrorTextWrapper'
import { AlertsSettings } from '../../../../settings/alertSettings'
import { RadioWrapper } from '../RadioWrapper/RadioWrapper'

const CREATE_alertTypeId_ALERT = (alertTypeId: AlertTypeId) => {
  if (alertTypeId === 'weatherForecastAlert') return 'CREATE_WEATHER_FORECAST_ALERT'

  return `CREATE_${constantCaseify(alertTypeId)}_ALERT`
}

const multiSelectDataBuilder = ({
  includeBlocks,
  includeStations,
  properties,
  stationTypeKey,
}: {
  includeBlocks: boolean
  includeStations: boolean
  properties: TProperty[]
  stationTypeKey: string
}) => {
  let stationsToReturn: TOptionsWithExternalVendors[] = []
  let blocksToReturn: TOptionsWithExternalVendors[] = []

  if (includeStations) {
    stationsToReturn = getPointsByValuesCurrentKey({
      properties: properties,
      key: stationTypeKey,
    })
      .map((station) => ({
        label: station.name,
        value: `${station.propertyId}&${station.geom}`,
        group: station.propertyName,
        id: station.geom,
        externalVendorNames: getPointExternalVendorNames({
          point: station,
          valueTypes: station.valuesCurrent.filter((valueType) => valueType.includes(stationTypeKey)),
        }),
      }))
      .sort(sortByKey('label'))
  }

  if (includeBlocks) {
    blocksToReturn = getBlocksFromProperties({ properties })
      .map((block) => ({
        label: block.name,
        value: `${block.propertyId}&${block.blockId}`,
        group: block.propertyName,
        id: String(block.blockId),
      }))
      .sort(sortByKey('label'))
  }

  return [...stationsToReturn, ...blocksToReturn].sort(sortByKey('group'))
}

export const WeatherForecastCoverage = ({
  coverageBlocks,
  coverageIsValid,
  coverageProperties,
  coverageStations,
  coverageType,
  isTemperatureTypeAlert,
  rule,
  setCoverageBlocks,
  setCoverageIsValid,
  setCoverageProperties,
  setCoverageStations,
  setCoverageType,
  setRule,
  triedToSubmit,
}: {
  coverageBlocks: string[]
  coverageIsValid: boolean
  coverageProperties: number[]
  coverageStations: string[]
  coverageType: string
  isTemperatureTypeAlert: boolean
  rule: Partial<WeatherForecastAlertOnly['rules']>
  setCoverageBlocks: Dispatch<SetStateAction<string[]>>
  setCoverageIsValid: Dispatch<SetStateAction<boolean>>
  setCoverageProperties: Dispatch<SetStateAction<number[]>>
  setCoverageStations: Dispatch<SetStateAction<string[]>>
  setCoverageType: Dispatch<SetStateAction<string>>
  setRule: Dispatch<SetStateAction<Partial<WeatherForecastAlertOnly['rules']>>>
  triedToSubmit: boolean
}) => {
  // includeAllDevices-handling
  const measurementHeights = 'measurementHeights' in rule ? rule.measurementHeights : []
  const includeAllDevices = 'includeAllDevices' in rule ? rule.includeAllDevices : false
  const typeIsAll = coverageType === 'all'
  const typeIsProperties = coverageType === 'properties'
  const typeIsStationsOrBlocks = coverageType === 'stationsOrBlocks'
  const stationTypeKey = AlertsSettings.stationTypeKey.weatherForecastAlert

  useEffect(() => {
    setCoverageIsValid(() => {
      let issueCount = 0

      if (coverageType === 'stationsOrBlocks') {
        issueCount += coverageStations.length === 0 ? 1 : 0
      }

      if (coverageType === 'properties') {
        issueCount += coverageProperties.length === 0 ? 1 : 0
      }

      if (isTemperatureTypeAlert) {
        issueCount += measurementHeights?.length === 0 && coverageType !== 'all' ? 1 : 0
      }

      return issueCount === 0
    })
  }, [coverageProperties, coverageStations, coverageType])

  let allWording = includeAllDevices
    ? translate.phrases.banyanApp('Stations/Blocks Across All Properties')
    : translate.phrases.banyanApp('Stations Across All Properties')

  const getHelpText = () => {
    switch (coverageType) {
      case 'properties':
        return translate.phrases.banyanApp('Please select at least one of your properties')

      case 'stationsOrBlocks':
        if (includeAllDevices)
          return translate.phrases.banyanApp('Please select at least one of your stations/blocks')

        return translate.phrases.banyanApp('Please select at least one of your stations')

      default:
        return ''
    }
  }

  let validateStatusError
  let helpText = ''

  if (!coverageIsValid && triedToSubmit && coverageType !== 'all') {
    helpText = getHelpText()

    validateStatusError = true
  }

  const properties = fieldAssetStore.useSelector((s) => s?.properties ?? {})

  const filteredPropertiesByPermission = Object.values(properties).filter((property: TProperty) => {
    return checkAuthorization({
      permission: CREATE_alertTypeId_ALERT('weatherForecastAlert') as TPermission,
      entity: property.propertyId,
    })
  })

  const propertiesData = useMemo(() => {
    return filteredPropertiesByPermission
      .map((property) => ({
        label: property.propertyName,
        value: property.propertyId.toString(),
      }))
      .sort(sortByKey('label'))
  }, [filteredPropertiesByPermission])

  const stationsOrBlocksData = useMemo(() => {
    return multiSelectDataBuilder({
      includeStations: true,
      includeBlocks: includeAllDevices ?? false,
      properties: filteredPropertiesByPermission,
      stationTypeKey,
    })
  }, [filteredPropertiesByPermission, includeAllDevices, stationTypeKey])

  const getSelectedData = (options: TOptions[], coverage: string[]) => {
    const selectedData = options.filter((option) => coverage.includes(option.value as string))

    return selectedData
  }

  return (
    <AlertSection title={translate.phrases.banyanApp('Coverage')}>
      <div css={{ display: 'grid', gridTemplateColumns: '100%', gridGap: 24 }}>
        <RadioWrapper checked={typeIsAll} onClick={() => setCoverageType('all')} title={allWording} />
        {/* PROPERTIES */}
        {!!AlertsSettings.allowCoverageProperties.weatherForecastAlert && (
          <RadioWrapper
            checked={typeIsProperties}
            onClick={() => setCoverageType('properties')}
            title={translate.phrases.banyanApp('Specific Properties')}
          >
            {typeIsProperties && (
              <MultiSelect
                data={propertiesData}
                placeholder={typeIsProperties ? translate.phrases.banyanApp('Select Properties') : ''}
                onChange={(selectedValues: TOptions[]) => {
                  setCoverageProperties(selectedValues.map((option) => option.value as number))

                  setCoverageStations([])

                  setCoverageBlocks([])
                }}
                selectedData={
                  typeIsProperties ? getSelectedData(propertiesData, coverageProperties.map(String)) : []
                }
                disabled={!typeIsProperties}
                error={validateStatusError && typeIsProperties}
              />
            )}
          </RadioWrapper>
        )}
        {/* STATIONS/BLOCKS/ALL-DEVICES */}
        {(() => {
          /**
           * we have to merge stations and blocks in the same dropdown. This can be a
           * bit confusing, but luckily station geoms are very long—50 character hex
           * strings—and block ids are comparatively short integers. The keys in the
           * tree follow the pattern "propertyId-stationGeom" or "propertyId-blockId"
           * so we can just assume from those. Note this only works assuming geoms for
           * stations and blockIds for blocks, and blockIds to be short, so this will
           * need to be changed if that reality doesn't hold anymore
           */
          const stationsOrBlocksOnChange = (newStationsOrBlocks: TOptions[]) => {
            const { newBlocks, newStations } = newStationsOrBlocks.reduce(
              (a, b) => {
                if ((b.value as string).split('&')[1].length > 30) a.newStations.push(b.value as string)
                else a.newBlocks.push(b.value as string)

                return a
              },
              {
                newBlocks: [] as string[],
                newStations: [] as string[],
              },
            )

            setCoverageBlocks(newBlocks)

            setCoverageStations(newStations)

            setCoverageProperties([])
          }

          if (includeAllDevices) {
            return (
              <RadioWrapper
                checked={typeIsStationsOrBlocks}
                onClick={() => setCoverageType('stationsOrBlocks')}
                title={translate.phrases.banyanApp('Specific Stations/Blocks')}
              >
                {typeIsStationsOrBlocks && (
                  <MultiSelect
                    data={stationsOrBlocksData}
                    {...makeExternalVendorMultiSelectProps({ data: stationsOrBlocksData })}
                    placeholder={
                      typeIsStationsOrBlocks ? translate.phrases.banyanApp('Select stations/blocks') : ''
                    }
                    onChange={(selectedValues: TOptions[]) => {
                      stationsOrBlocksOnChange(selectedValues)
                    }}
                    selectedData={
                      typeIsStationsOrBlocks
                        ? getSelectedData(stationsOrBlocksData, [...coverageStations, ...coverageBlocks])
                        : []
                    }
                    groupOptions={true}
                    disabled={!typeIsStationsOrBlocks}
                    error={validateStatusError && typeIsStationsOrBlocks}
                  />
                )}
              </RadioWrapper>
            )
          }

          if (!!AlertsSettings.allowCoverageStations.weatherForecastAlert) {
            return (
              <RadioWrapper
                checked={typeIsStationsOrBlocks}
                onClick={() => setCoverageType('stationsOrBlocks')}
                title={translate.phrases.banyanApp('Specific Stations')}
              >
                {typeIsStationsOrBlocks && (
                  <MultiSelect
                    data={stationsOrBlocksData}
                    {...makeExternalVendorMultiSelectProps({ data: stationsOrBlocksData })}
                    placeholder={typeIsStationsOrBlocks ? translate.phrases.banyanApp('Select stations') : ''}
                    onChange={(selectedValues: TOptions[]) => {
                      stationsOrBlocksOnChange(selectedValues)
                    }}
                    selectedData={
                      typeIsStationsOrBlocks ? getSelectedData(stationsOrBlocksData, coverageStations) : []
                    }
                    groupOptions={true}
                    disabled={!typeIsStationsOrBlocks}
                    error={validateStatusError && typeIsStationsOrBlocks}
                  />
                )}
              </RadioWrapper>
            )
          }

          // in cases with nothing more granular than property—e.g. trapCatches—return null here
          return null
        })()}

        {isTemperatureTypeAlert && (
          <>
            <div>{translate.phrases.banyanApp('At the following measurement height:')}</div>
            <MantineCheckBox.Group
              onChange={(val) => {
                setRule((prev) => ({
                  ...prev,
                  measurementHeights: val as ('aboveCanopyTemperature' | 'inCanopyTemperature')[],
                }))
              }}
              value={measurementHeights}
            >
              <Checkbox
                value="aboveCanopyTemperature"
                label="Above Canopy Temperature"
                css={{ marginBottom: 15 }}
              />
              <Checkbox value="inCanopyTemperature" label="In Canopy Temperature" />
            </MantineCheckBox.Group>

            <Checkbox
              checked={!!includeAllDevices}
              onChange={() =>
                setRule((prev) => ({
                  ...prev,
                  includeAllDevices: !includeAllDevices,
                }))
              }
              label={translate.phrases.banyanApp('Include all devices')}
            />
          </>
        )}

        {helpText && <ErrorTextWrapper>{helpText}</ErrorTextWrapper>}
      </div>
    </AlertSection>
  )
}
